import React from 'react';
import { ArrowRight, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { projectsData } from '../data/mock';

const Projects = () => {
  return (
    <section id="projects" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-red-100 text-red-700 hover:bg-red-200 px-4 py-1 text-sm font-semibold">
            {projectsData.subtitle}
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {projectsData.title}
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Real results from real partnerships. See how we've helped brands grow.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projectsData.projects.map((project, index) => (
            <Card
              key={project.id}
              className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-slate-200 hover:border-cyan-300 overflow-hidden bg-white"
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              {/* Project Image */}
              <div className="relative h-56 overflow-hidden bg-slate-100">
                <img
                  src={project.image}
                  alt={project.clientName}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <CardContent className="p-6">
                {/* Client Name */}
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors">
                  {project.clientName}
                </h3>

                {/* Work Type */}
                <p className="text-sm font-semibold text-red-700 mb-3">
                  {project.workType}
                </p>

                {/* Result */}
                <p className="text-slate-600 leading-relaxed mb-6">
                  {project.result}
                </p>

                {/* Metrics */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.entries(project.metrics).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-center gap-1 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-200"
                    >
                      <TrendingUp className="w-3.5 h-3.5 text-red-600" />
                      <span className="text-xs font-semibold text-slate-700">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* View Details Button */}
                <Button
                  variant="ghost"
                  className="w-full group/btn hover:bg-red-50 hover:text-red-700 transition-colors"
                >
                  View Case Study
                  <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-slate-600 mb-6 text-lg">
            Want to see your brand here? Let's create something amazing together.
          </p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg shadow-red-500/30 hover:shadow-xl hover:scale-105 transition-all duration-300 px-8"
          >
            Start Your Project
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Projects;
